import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  FormHelperText,
  Typography,
  Paper,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import { Business as BusinessIcon, Edit as EditIcon } from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../../common/CommonLoader";
import ClientSelectorDialog from "../../common/ClientSelectorDialog"; // Adjust path as needed
import {
  SaveWaterProfile,
  UpdateWaterProfile,
  GetWaterProfileById,
  CheckTypeAvailability, // New service function for checking type availability
} from "../../../services/ClientWaterProfileService";

const AddWaterProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // State for all form fields
  const [formData, setFormData] = useState({
    accountNumber: "",
    type: "",
    calcium: null,
    totalAlkalinity: null,
    bicarbonate: null,
    sulfate: null,
    magnesium: null,
    totalHardness: null,
    iron: null,
    pH: null,
    totalChlorine: null,
    residualAlkalinity: null,
    chloride: null,
    sulfateToChlorideRatio: null,
    sodium: null,
    zinc: null,
    potassium: null,
    copper: null,
    tds: null,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [accountValid, setAccountValid] = useState(false);
  const [checkingType, setCheckingType] = useState(false);
  const [typeValidationStatus, setTypeValidationStatus] = useState(null); // 'available', 'unavailable', or null
  
  // New states for ClientSelectorDialog
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (id) {
      fetchProfileData(parseInt(id, 10));
    }
  }, [id]);

  // New useEffect for type validation
  useEffect(() => {
    const checkTypeAvailability = async () => {
      if (
        !id &&
        accountValid &&
        formData.accountNumber &&
        formData.type.trim()
      ) {
        setCheckingType(true);
        setTypeValidationStatus(null);

        try {
          // Add delay before API call
          const timeoutId = setTimeout(async () => {
           
            try {
              const response = await CheckTypeAvailability({
                accountNumber: String(formData.accountNumber),
                type: String(formData.type),
              });

              if (
                response &&
                response.statusCode === 200 &&
                response.results === true
              ) {
                setTypeValidationStatus("available");
                setErrors((prev) => ({ ...prev, type: "" }));
              } else {
                setTypeValidationStatus("unavailable");
                setErrors((prev) => ({
                  ...prev,
                  type: "This type/water profile name is not available for this client. Please try a different name.",
                }));
              }
            } catch (error) {
              setTypeValidationStatus("unavailable");
              setErrors((prev) => ({
                ...prev,
                type: "Not Available. Please try a different name.",
              }));
            } finally {
              setCheckingType(false);
            }
          }, 500); // 500ms delay

          return () => clearTimeout(timeoutId);
        } catch (error) {
          setCheckingType(false);
        }
      } else if (!accountValid || !formData.type.trim()) {
        setTypeValidationStatus(null);
        setCheckingType(false);
      }
    };

    checkTypeAvailability();
  }, [accountValid, formData.accountNumber, formData.type, id]);

  // Reset type validation when account changes
  useEffect(() => {
    if (!id && !accountValid) {
      setFormData((prev) => ({ ...prev, type: "" }));
      setTypeValidationStatus(null);
      setCheckingType(false);
    }
  }, [accountValid, id]);

  const fetchProfileData = async (profileId) => {
    setLoading(true);
    try {
      const response = await GetWaterProfileById(profileId);
      if (response && response.results) {
        setFormData(response.results);
      } else {
        NotificationManager.error("Failed to fetch profile data");
      }
    } catch (error) {
      NotificationManager.error("Error fetching profile data");
    } finally {
      setLoading(false);
    }
  };

  // Handle client selection from dialog
  const handleClientSelect = (client) => {
    if (client) {
      setSelectedClient(client);
      setFormData(prev => ({
        ...prev,
        accountNumber: client.accountNumber
      }));
      setAccountValid(true);
      // Clear any previous account-related errors
      setErrors(prev => ({
        ...prev,
        accountNumber: ""
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.accountNumber)
      newErrors.accountNumber = "Account number is required";
    if (!formData.type) newErrors.type = "Type is required";

    // Don't allow saving if type is not available
    if (!id && typeValidationStatus === "unavailable") {
      newErrors.type =
        "This type/water profile name is not available for this client. Please try a different name.";
    }

    // Validate numeric fields if needed
    const numericFields = [
      "calcium",
      "totalAlkalinity",
      "bicarbonate",
      "sulfate",
      "magnesium",
      "totalHardness",
      "iron",
      "pH",
      "totalChlorine",
      "residualAlkalinity",
      "chloride",
      "sulfateToChlorideRatio",
      "sodium",
      "zinc",
      "potassium",
      "copper",
      "tds",
    ];

    numericFields.forEach((field) => {
      if (formData[field] && isNaN(formData[field])) {
        newErrors[field] = "Must be a valid number";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Additional check to prevent saving if type is still being validated or unavailable
    if (!id && (checkingType || typeValidationStatus === "unavailable")) {
      NotificationManager.warning(
        "Please wait for type validation to complete or choose a different type name."
      );
      return;
    }

    const profileData = {
      ...formData,
      accountNumber: String(formData.accountNumber || ""),
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id) {
        await UpdateWaterProfile(profileData);
        NotificationManager.success("Profile updated successfully");
      } else {
        await SaveWaterProfile(profileData);
        NotificationManager.success("Profile added successfully");
      }
      navigate("/water-profiles");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value === "" ? null : Number(value) || value,
    }));
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: value,
    }));

    // Reset validation status when user types
    if (value.trim()) {
      setTypeValidationStatus(null);
    }
  };

  const handleCancel = () => {
    navigate("/water-profiles");
  };

  // Helper function to get type field status
  const getTypeFieldStatus = () => {
    if (checkingType)
      return { color: "info", text: "Checking availability..." };
    if (typeValidationStatus === "available")
      return { color: "success", text: "Type name is available" };
    if (typeValidationStatus === "unavailable")
      return { color: "error", text: errors.type };
    return null;
  };

  const typeStatus = getTypeFieldStatus();

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Water Profile" : "Add New Water Profile"}
        </Typography>
        <Grid container spacing={2}>
          {/* Account Number and Type */}
          {id ? (
            <>
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="body1">
                  <strong>Account Number:</strong> {formData.accountNumber}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="body1">
                  <strong>Type:</strong> {formData.type}
                </Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} sm={4} md={3}>
                {selectedClient ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Account Number"
                      value={formData.accountNumber}
                      size="small"
                      fullWidth
                      disabled
                    />
                    <IconButton
                      onClick={() => setClientDialogOpen(true)}
                      color="primary"
                      title="Change Client"
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setClientDialogOpen(true)}
                      startIcon={<BusinessIcon />}
                      sx={{ height: 56 }}
                    >
                      Add Client
                    </Button>
                    {errors.accountNumber && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.accountNumber}
                      </FormHelperText>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  label="Water Profile Type"
                  value={formData.type}
                  onChange={handleTypeChange}
                  size="small"
                  fullWidth
                  required
                  disabled={!accountValid} // Disable until account is valid
                  error={!!errors.type}
                  helperText={typeStatus ? typeStatus.text : errors.type}
                  InputProps={{
                    endAdornment: checkingType ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              </Grid>
            </>
          )}

          {/* Chemical Properties */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Chemical Properties (mg/L)
            </Typography>
          </Grid>

          {[
            { field: "calcium", label: "Calcium" },
            { field: "totalAlkalinity", label: "Total Alkalinity" },
            { field: "bicarbonate", label: "Bicarbonate" },
            { field: "sulfate", label: "Sulfate" },
            { field: "magnesium", label: "Magnesium" },
            { field: "totalHardness", label: "Total Hardness" },
            { field: "iron", label: "Iron" },
            { field: "pH", label: "pH" },
            { field: "totalChlorine", label: "Total Chlorine" },
            { field: "residualAlkalinity", label: "Residual Alkalinity" },
            { field: "chloride", label: "Chloride" },
            {
              field: "sulfateToChlorideRatio",
              label: "Sulfate to Chloride Ratio",
            },
            { field: "sodium", label: "Sodium" },
            { field: "zinc", label: "Zinc" },
            { field: "potassium", label: "Potassium" },
            { field: "copper", label: "Copper" },
            { field: "tds", label: "TDS" },
          ].map(({ field, label }) => (
            <Grid item xs={6} sm={4} md={3} key={field}>
              <TextField
                label={label}
                type="number"
                size="small"
                value={formData[field] ?? ""}
                onChange={handleChange(field)}
                fullWidth
                InputProps={{ inputProps: { step: "0.01" } }}
                error={!!errors[field]}
                helperText={errors[field]}
              />
            </Grid>
          ))}

          {/* Action Buttons */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button
                onClick={handleCancel}
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleSave}
                color="primary"
                variant="contained"
                disabled={
                  !id &&
                  (checkingType || typeValidationStatus === "unavailable")
                }
              >
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Client Selector Dialog */}
      <ClientSelectorDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSelect={handleClientSelect}
        multiSelect={false}
      />

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddWaterProfile;